import { DecimalPipe, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  Inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { adaptEchartsOptions, SliderComponent, watchBodyDarkMode$ } from '@examdojo/core/widget';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

echarts.use([BarChart, SVGRenderer, GridComponent, TooltipComponent]);

export interface PoissonExplorerParams {
  lambda: number;
  maxK: number;
  chartHeight?: number;
}

const DEFAULT_PARAMS: PoissonExplorerParams = {
  lambda: 4,
  maxK: 15,
  chartHeight: 300,
};

const ORANGE = '#f97316';

function poissonPmf(k: number, lambda: number): number {
  let logP = -lambda + k * Math.log(lambda);
  for (let i = 2; i <= k; i++) {
    logP -= Math.log(i);
  }
  return Math.exp(logP);
}

@Component({
  selector: 'dojo-poisson-explorer',
  standalone: true,
  imports: [NgxEchartsDirective, SliderComponent, DecimalPipe],
  templateUrl: './poisson-explorer.component.html',
  styleUrl: './poisson-explorer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [provideEchartsCore({ echarts })],
  host: { class: 'block' },
})
export class PoissonExplorerComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });

    effect(
      () => {
        const p = this.params();
        this.lambda.set(p.lambda);
        this.maxK.set(p.maxK);
      },
      { allowSignalWrites: true },
    );
  }

  private readonly isDark;

  readonly initOpts = { renderer: 'svg' } as const;
  readonly params = input<PoissonExplorerParams>(DEFAULT_PARAMS);

  readonly lambda = signal(DEFAULT_PARAMS.lambda);
  readonly maxK = signal(DEFAULT_PARAMS.maxK);

  readonly chartHeight = computed(() => this.params().chartHeight ?? 300);

  readonly mean = computed(() => this.lambda());
  readonly variance = computed(() => this.lambda());

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly chartOptions = computed<any>(() => {
    const lam = this.lambda();
    const max = this.maxK();
    const categories: string[] = [];
    const data: number[] = [];

    for (let k = 0; k <= max; k++) {
      categories.push(k.toString());
      data.push(poissonPmf(k, lam));
    }

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: Array<{ name: string; value: number }>) => {
          const p = params[0];
          return `P(X = ${p.name}) = ${p.value.toFixed(4)}`;
        },
      },
      grid: {
        left: 50,
        right: 20,
        top: 20,
        bottom: 40,
      },
      xAxis: {
        type: 'category',
        data: categories,
        name: 'k',
        nameLocation: 'middle',
        nameGap: 25,
      },
      yAxis: {
        type: 'value',
        name: 'P(X = k)',
        nameLocation: 'middle',
        nameGap: 35,
        max: (value: { max: number }) => Math.min(Math.ceil(value.max * 100) / 100 + 0.02, 1),
      },
      series: [
        {
          type: 'bar',
          data,
          itemStyle: {
            color: ORANGE,
            borderRadius: [2, 2, 0, 0],
          },
          barMaxWidth: 30,
        },
      ],
    };
  });

  readonly adaptedOptions = computed(() => adaptEchartsOptions(this.chartOptions(), this.document, this.isDark()));

  readonly onLambdaChange = (value: number): void => {
    this.lambda.set(Math.max(0.1, value));
  };
}
